import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Popover,
  Button,
  Typography,
} from '@mui/material';
import { Clear, Close } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { DocumentDataStatusFilters } from '../../types/packager-types';
import { toLocalDatetimeInput } from '../../utils/datetime';
import { filterInputSx } from '../common/filterStyles';
import { splitByDelimiters } from '../../utils/stringUtils';

const chipInputSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 32,
    fontSize: '12.5px',
    borderRadius: '6px',
    backgroundColor: '#fff',
    py: '2px !important',
    px: '8px !important',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.24)' },
    '&.Mui-focused fieldset': { borderColor: '#1D9AAA', borderWidth: 1 },
    '& .MuiAutocomplete-tag': { m: '1px', maxWidth: '100px' },
    '& .MuiAutocomplete-input': { minWidth: '60px !important', fontSize: '12.5px', p: '2px 4px !important' },
  },
  '& .MuiInputLabel-root': {
    fontSize: '12.5px',
    color: '#5f6d7e',
    '&.Mui-focused': { color: '#1D9AAA' },
  },
};

/** Split comma-separated string to array, filtering empties */
const csvToArray = (val: string): string[] =>
  val ? val.split(',').map((s) => s.trim()).filter(Boolean) : [];

/** Join array back to comma-separated string */
const arrayToCsv = (arr: string[]): string => arr.join(',');

/** Reusable multi-value chip autocomplete with onBlur/onPaste support */
interface MultiValueChipInputProps {
  label: string;
  value: string[];
  onChange: (newValue: string[]) => void;
  dataCy: string;
  /** If true (default), splits on commas AND spaces. If false, only commas (for phrase fields). */
  splitOnSpaces?: boolean;
}

const MultiValueChipInput: React.FC<MultiValueChipInputProps> = ({ label, value, onChange, dataCy, splitOnSpaces = true }) => {
  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      size="small"
      value={value}
      onChange={(_e, newVal, reason) => {
        if (reason === 'removeOption' || reason === 'clear') {
          onChange(newVal as string[]);
          return;
        }
        if (reason === 'createOption' && newVal.length > 0) {
          const lastVal = newVal[newVal.length - 1] as string;
          const existingVals = newVal.slice(0, -1) as string[];
          const parsedVals = splitByDelimiters(lastVal, splitOnSpaces);
          const allVals = [...new Set([...existingVals, ...parsedVals])];
          onChange(allVals);
        }
      }}
      onBlur={(e) => {
        const inputVal = (e.target as HTMLInputElement).value?.trim();
        if (inputVal) {
          const parsedVals = splitByDelimiters(inputVal, splitOnSpaces);
          if (parsedVals.length > 0) {
            const allVals = [...new Set([...value, ...parsedVals])];
            onChange(allVals);
          }
        }
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            label={option}
            size="small"
            sx={{
              height: 20,
              fontSize: '11px',
              bgcolor: 'rgba(29,154,170,0.08)',
              color: '#1a2332',
              '& .MuiChip-deleteIcon': { fontSize: 14, color: '#5f6d7e' },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? (splitOnSpaces ? 'Comma or space separated' : 'Comma separated') : ''}
          variant="outlined"
          data-cy={dataCy}
          onPaste={(e) => {
            const pastedText = e.clipboardData.getData('text');
            // For splitOnSpaces=true, intercept if comma or space; for false, only comma
            const shouldIntercept = splitOnSpaces
              ? pastedText.includes(',') || pastedText.includes(' ')
              : pastedText.includes(',');
            if (shouldIntercept) {
              e.preventDefault();
              const parsedVals = splitByDelimiters(pastedText, splitOnSpaces);
              if (parsedVals.length > 0) {
                const allVals = [...new Set([...value, ...parsedVals])];
                onChange(allVals);
              }
            }
          }}
        />
      )}
      sx={chipInputSx}
    />
  );
};

export interface DocFilterValues {
  has_been_sent: string;
  is_supported: string;
  created_after: string;
  created_before: string;
  format_id: string;
  exclude_id: string;
  name: string;
  exclude_name: string;
  language: string;
  exclude_language: string;
  duplicate_key: string;
  exclude_duplicate_key: string;
  document_data_status: string;
}

export const emptyDocFilters: DocFilterValues = {
  has_been_sent: '',
  is_supported: '',
  created_after: '',
  created_before: '',
  format_id: '',
  exclude_id: '',
  name: '',
  exclude_name: '',
  language: '',
  exclude_language: '',
  duplicate_key: '',
  exclude_duplicate_key: '',
  document_data_status: '',
};

export const DOC_FILTER_KEYS = Object.keys(emptyDocFilters) as (keyof DocFilterValues)[];

/** Convert URL param values into popover draft values (dates need local conversion) */
export function urlToDocDraft(params: URLSearchParams): DocFilterValues {
  const get = (key: string) => params.get(key) || '';
  return {
    has_been_sent: get('has_been_sent'),
    is_supported: get('is_supported'),
    created_after: get('created_after') ? toLocalDatetimeInput(get('created_after')) : '',
    created_before: get('created_before') ? toLocalDatetimeInput(get('created_before')) : '',
    format_id: get('format_id'),
    exclude_id: get('exclude_id'),
    name: get('name'),
    exclude_name: get('exclude_name'),
    language: get('language'),
    exclude_language: get('exclude_language'),
    duplicate_key: get('duplicate_key'),
    exclude_duplicate_key: get('exclude_duplicate_key'),
    document_data_status: get('document_data_status'),
  };
}

/** Convert draft values back to URL param values (dates need ISO conversion) */
export function docDraftToUrlParams(draft: DocFilterValues): Record<string, string> {
  const result: Record<string, string> = {};
  for (const key of DOC_FILTER_KEYS) {
    let val = draft[key].trim();
    if (!val) continue;
    if (key === 'created_after' || key === 'created_before') {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        val = d.toISOString();
      } else {
        continue;
      }
    }
    result[key] = val;
  }
  return result;
}

const SELECT_FIELDS: { key: keyof DocFilterValues; label: string }[] = [
  { key: 'has_been_sent', label: 'Has Been Sent' },
  { key: 'is_supported', label: 'Is Supported' },
];

const PAIRED_TEXT_FIELDS: {
  includeKey: keyof DocFilterValues;
  excludeKey: keyof DocFilterValues;
  includeLabel: string;
  excludeLabel: string;
  splitOnSpaces?: boolean; // Default true; set false for phrase fields like "Name Contains"
}[] = [
  { includeKey: 'name', excludeKey: 'exclude_name', includeLabel: 'Name Contains', excludeLabel: 'Name Excludes', splitOnSpaces: false },
  { includeKey: 'language', excludeKey: 'exclude_language', includeLabel: 'Language Include', excludeLabel: 'Language Exclude' },
  { includeKey: 'duplicate_key', excludeKey: 'exclude_duplicate_key', includeLabel: 'Dup Key Include', excludeLabel: 'Dup Key Exclude' },
];

// Doc ID Exclude is standalone since Doc ID Include is now in the main filter bar
const STANDALONE_EXCLUDE_FIELDS: { key: keyof DocFilterValues; label: string }[] = [
  { key: 'exclude_id', label: 'Doc ID Exclude' },
];

interface DocumentFiltersPopoverProps {
  anchorEl: HTMLElement | null;
  currentValues: DocFilterValues;
  onClose: () => void;
  onApply: (values: DocFilterValues) => void;
}

const DocumentFiltersPopover = ({
  anchorEl,
  currentValues,
  onClose,
  onApply,
}: DocumentFiltersPopoverProps) => {
  const [draft, setDraft] = useState<DocFilterValues>(currentValues);
  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    const isOpen = Boolean(anchorEl);
    if (isOpen && !wasOpenRef.current) {
      setDraft(currentValues);
    }
    wasOpenRef.current = isOpen;
  }, [anchorEl, currentValues]);

  const handleReset = () => setDraft(emptyDocFilters);

  const renderClearButton = (key: keyof DocFilterValues) =>
    draft[key] ? (
      <InputAdornment position="end">
        <IconButton
          size="small"
          onClick={() => setDraft((prev) => ({ ...prev, [key]: '' }))}
          edge="end"
          sx={{ mr: -1, p: 0.25 }}
        >
          <Clear sx={{ fontSize: 14 }} />
        </IconButton>
      </InputAdornment>
    ) : undefined;

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '8px', p: 2, width: 500 } } }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onApply(draft);
        }}
        onKeyDown={(e) => {
          // Prevent Enter inside Autocomplete chip inputs from submitting the form
          if (e.key === 'Enter' && (e.target as HTMLElement).closest('.MuiAutocomplete-root')) {
            e.preventDefault();
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {/* Boolean select fields */}
          {SELECT_FIELDS.map(({ key, label }) => (
            <TextField
              key={key}
              select
              size="small"
              label={label}
              variant="outlined"
              value={draft[key] || ''}
              onChange={(e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }))}
              data-cy={`filter-${key}`}
              sx={filterInputSx}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          ))}

          {/* Date fields */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Created After"
              value={draft.created_after ? dayjs(draft.created_after) : null}
              referenceDate={dayjs().hour(0).minute(0).second(0)}
              timeSteps={{ minutes: 1 }}
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDraft((prev) => ({
                    ...prev,
                    created_after: date.format('YYYY-MM-DDTHH:mm'),
                  }));
                } else {
                  setDraft((prev) => ({ ...prev, created_after: '' }));
                }
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  sx: filterInputSx,
                  'data-cy': 'filter-created-after',
                } as any,
                field: {
                  clearable: true,
                  onClear: () => setDraft((prev) => ({ ...prev, created_after: '' })),
                },
              }}
            />
            <DateTimePicker
              label="Created Before"
              value={draft.created_before ? dayjs(draft.created_before) : null}
              referenceDate={dayjs().hour(23).minute(59).second(59)}
              timeSteps={{ minutes: 1 }}
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDraft((prev) => ({
                    ...prev,
                    created_before: date.format('YYYY-MM-DDTHH:mm'),
                  }));
                } else {
                  setDraft((prev) => ({ ...prev, created_before: '' }));
                }
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  sx: filterInputSx,
                  'data-cy': 'filter-created-before',
                } as any,
                field: {
                  clearable: true,
                  onClear: () => setDraft((prev) => ({ ...prev, created_before: '' })),
                },
              }}
            />
          </LocalizationProvider>

          {/* Format ID */}
          <TextField
            size="small"
            placeholder="Format ID"
            label="Format ID"
            variant="outlined"
            autoComplete="off"
            value={draft.format_id}
            onChange={(e) => setDraft((prev) => ({ ...prev, format_id: e.target.value }))}
            data-cy="filter-format_id"
            sx={filterInputSx}
            InputProps={{ endAdornment: renderClearButton('format_id') }}
          />

          {/* Standalone Doc ID Exclude (Doc ID Include is in main filter bar) */}
          {STANDALONE_EXCLUDE_FIELDS.map(({ key, label }) => (
            <MultiValueChipInput
              key={key}
              label={label}
              value={csvToArray(draft[key])}
              onChange={(newVal) => setDraft((prev) => ({ ...prev, [key]: arrayToCsv(newVal) }))}
              dataCy={`filter-${key}`}
            />
          ))}

          <TextField
            select
            size="small"
            label="Data Status"
            variant="outlined"
            value={draft.document_data_status || ''}
            onChange={(e) => setDraft((prev) => ({ ...prev, document_data_status: e.target.value }))}
            data-cy="filter-document_data_status"
            sx={filterInputSx}
          >
            <MenuItem value="">Any</MenuItem>
            {Object.values(DocumentDataStatusFilters).map((s) => (
              <MenuItem key={s} value={s}>
                {s.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </TextField>

          {/* Paired include/exclude chip inputs */}
          {PAIRED_TEXT_FIELDS.map(({ includeKey, excludeKey, includeLabel, excludeLabel, splitOnSpaces }) => (
            <React.Fragment key={includeKey}>
              <MultiValueChipInput
                label={includeLabel}
                value={csvToArray(draft[includeKey])}
                onChange={(newVal) => setDraft((prev) => ({ ...prev, [includeKey]: arrayToCsv(newVal) }))}
                dataCy={`filter-${includeKey}`}
                splitOnSpaces={splitOnSpaces}
              />
              <MultiValueChipInput
                label={excludeLabel}
                value={csvToArray(draft[excludeKey])}
                onChange={(newVal) => setDraft((prev) => ({ ...prev, [excludeKey]: arrayToCsv(newVal) }))}
                dataCy={`filter-${excludeKey}`}
                splitOnSpaces={splitOnSpaces}
              />
            </React.Fragment>
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5 }}>
          <Button
            size="small"
            type="button"
            onClick={handleReset}
            data-cy="btn-reset-filters"
            sx={{
              textTransform: 'none',
              fontSize: '12.5px',
              fontWeight: 500,
              height: 34,
              borderRadius: '6px',
              color: '#5f6d7e',
              border: '1px solid rgba(0,0,0,0.12)',
              '&:hover': { borderColor: 'rgba(0,0,0,0.24)', backgroundColor: 'rgba(0,0,0,0.02)' },
            }}
          >
            Reset
          </Button>
          <Button
            size="small"
            type="submit"
            variant="contained"
            data-cy="btn-apply-filters"
            sx={{
              textTransform: 'none',
              fontSize: '12.5px',
              fontWeight: 500,
              height: 34,
              borderRadius: '6px',
              backgroundColor: '#1D9AAA',
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#178a99', boxShadow: 'none' },
            }}
          >
            Apply
          </Button>
        </Box>
      </form>
    </Popover>
  );
};

export default DocumentFiltersPopover;
